import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselIndicators,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import type { CMSColumn } from '../schemas';
import { match } from 'ts-pattern';
import { CMSCard } from '../cms-card';
import { cn } from '@/lib/utils';
import { useResponsive } from '@/hooks/use-responsive';
import { useResize } from '@/hooks/use-resize';
import { extractArticleIds } from '@/utils/cms';
import { cmsQueryKeys } from '@/services/cms/cmsQueryKeys';
import { getBlogContentById } from '@/services/cms/getBlogContentById';
import { useQuery } from '@tanstack/react-query';
import { BlogCard } from '@/components/blog/blog-card';

interface CMSColumnsCarouselProps {
  data: CMSColumn;
  className?: string;
  isFromBannerWithImage?: boolean;
}
const CMSColumnsCarousel = ({
  data,
  className,
  isFromBannerWithImage = false,
}: CMSColumnsCarouselProps) => {
  const overflowBehavior = data.overflow_behaviour.key;
  const { isDesktop, isTablet } = useResponsive();
  const isOverflow = overflowBehavior === 'overflow';

  const blogIds = extractArticleIds(data);

  const {
    isError,
    isPending,
    data: blogData,
  } = useQuery({
    queryKey: [...cmsQueryKeys.getBlogContentById, ...blogIds],
    queryFn: () => getBlogContentById({ blogIds }),
    staleTime: Infinity,
    enabled: blogIds.length > 0,
  });

  const maxPerRow = data.max_per_row;
  const itemsCount = data.items.length;

  const isOverflowEnabled = (isDesktop || isTablet) && isOverflow;

  const { ref, spacingLeftComputation } = useResize({
    isEnabled: isOverflowEnabled,
  });
  const styles = isOverflowEnabled
    ? { marginLeft: `${spacingLeftComputation}px` }
    : {};

  if (isError || (isPending && blogIds.length > 0)) {
    return null;
  }
  return (
    <>
      {/* use to calculate padding and margin left of container */}
      {isOverflowEnabled && (
        <div
          data-component="CMSColumnsCarousel"
          ref={ref}
          className="invisible md:container"
        ></div>
      )}
      <Carousel
        opts={{
          align: 'center',
          breakpoints: {
            '(min-width: 768px)': {
              // 'md' => Tablet breakpoint
              slidesToScroll: maxPerRow > 2 ? 2 : maxPerRow,
              active: maxPerRow > 2 ? itemsCount > 2 : itemsCount > maxPerRow,
            },
            '(min-width: 1280px)': {
              // 'lg' => Desktop breakpoint
              slidesToScroll: maxPerRow,
              active: itemsCount > maxPerRow,
            },

            //Breakpoints for overflow tablet
            ...(isOverflow && {
              '(min-width: 768px)': {
                slidesToScroll: 'auto',
                active: itemsCount > 2,
                dragFree: true,
              },
              '(min-width: 1008px)': {
                slidesToScroll: 'auto',
                active: itemsCount > 3,
                dragFree: true,
              },
              //disable by default, if below condition are true it will be overwritten.
              '(min-width: 1280px)': {
                slidesToScroll: 'auto',
                active: false,
                dragFree: true,
              },
            }),
            // Breakpoints for overflow and max per row 3
            ...(isOverflow &&
              maxPerRow === 3 && {
                '(min-width: 1280px)': {
                  slidesToScroll: 'auto',
                  active: itemsCount > 3,
                  dragFree: true,
                },
                '(min-width: 1908px)': {
                  slidesToScroll: 'auto',
                  active: itemsCount > 4,
                  dragFree: true,
                },
                '(min-width: 2667px)': {
                  slidesToScroll: 'auto',
                  active: itemsCount > 5,
                  dragFree: true,
                },
                '(min-width: 3427px)': {
                  slidesToScroll: 'auto',
                  active: itemsCount > 6,
                  dragFree: true,
                },
              }),

            // Breakpoints for overflow and max per row 4
            ...(isOverflow &&
              maxPerRow === 4 && {
                '(min-width: 1280px)': {
                  slidesToScroll: 'auto',
                  active: itemsCount > 4,
                  dragFree: true,
                },
                '(min-width: 1743px)': {
                  slidesToScroll: 'auto',
                  active: itemsCount > 5,
                  dragFree: true,
                },
                '(min-width: 2323px)': {
                  slidesToScroll: 'auto',
                  active: itemsCount > 6,
                  dragFree: true,
                },
                '(min-width: 2903px)': {
                  slidesToScroll: 'auto',
                  active: itemsCount > 7,
                  dragFree: true,
                },
              }),
          },
        }}
        className={cn(
          'w-full pl-5 py-6 md:py-12 lg:py-16',
          {
            'container md:container': !isOverflow,
            'pl-0': maxPerRow === 4 && !isOverflow,
            'pl-0 md:pl-0': isOverflow,
          },
          className
        )}
        data-component="CMSColumnsCarousel"
      >
        <CarouselContent
          style={styles}
          className={cn('-ml-3 md:-ml-8', {
            'lg:justify-center':
              maxPerRow === 3 && itemsCount <= 2 && !isOverflow,
            'lg:-ml-6': maxPerRow === 3 && !isOverflow,
            'ml-1': maxPerRow === 4 && !isOverflow,
            [`ml-[3px] mr-2 md:mr-5`]: isOverflow,
          })}
        >
          {data.items.map((item, index) => (
            <CarouselItem
              key={index}
              className={cn(
                'md:basis-1/2 lg:basis-1/4 select-none pl-3 md:pl-8',
                {
                  // Show partial next slide only if there's more than one item
                  'basis-[83%]': itemsCount > 1,
                  'basis-full': itemsCount === 1,

                  'md:basis-full lg:basis-full': !isOverflow && maxPerRow === 1,

                  'md:basis-1/2 lg:basis-1/2': !isOverflow && maxPerRow === 2,

                  //This set 258px width for cards
                  'basis-[90%] lg:basis-[290px] lg:pl-8':
                    !isOverflow && maxPerRow === 4,

                  //this set 258px for cards
                  // we will show 2.5 cards for tablet breakpoint if isOverflow set to true
                  'md:pl-8 md:basis-[290px]': isOverflow,
                  'md:pl-0 md:basis-[258px] ': isOverflow && index === 0,
                  //this set 360px width for cards
                  //add padding left side on each card(serve as gap)
                  'lg:basis-[384px] lg:pl-6': maxPerRow === 3 && !isOverflow,

                  //this set 210px(starting) for small 360px width for cards in banner with image
                  'basis-[72.2%] pl-[13px]': isOverflow,

                  //360px width cards for maxPer row 3 and isOverflow is set, desktop breakpoint. we only show 3.5 cards starting from 1440px breakpoint.
                  'lg:basis-[380px] lg:pl-5': isOverflow && maxPerRow === 3,
                  'lg:pl-0 lg:basis-[360px]':
                    isOverflow && index === 0 && maxPerRow === 3,

                  //258px width cards for maxPer row 4 and isOverflow is set, desktop breakpoint. we only show 4.5 cards starting from 1440px breakpoint.
                  'lg:pl-8 lg:basis-[290px]': isOverflow && maxPerRow === 4,
                  'lg:pl-0 lg:basis-[258px] ':
                    isOverflow && index === 0 && maxPerRow === 4,
                }
              )}
            >
              {match(item)
                .with({ type: 'card' }, (item) => (
                  <CMSCard
                    isFromBannerWithImage={isFromBannerWithImage}
                    cardData={item}
                    className={cn(
                      'h-full',

                      {
                        //to have outline/border white for CMS vertical card component
                        //NOTE: using border-white doesn't get applied while using arbitrary selector
                        '[&[data-component="CMSCard"]]:border [&[data-component="CMSCard"]]:border-grey-50':
                          !isFromBannerWithImage &&
                          maxPerRow === 3 &&
                          !isOverflow,
                      }
                    )}
                  />
                ))
                .with({ type: 'article_card' }, (item) => {
                  const blogCardData = blogData?.find(
                    (blogItem) => blogItem.id === item.content.id
                  );
                  if (!blogCardData) {
                    return null;
                  }
                  return (
                    <BlogCard
                      key={item.id}
                      className="h-full"
                      blogCardData={blogCardData}
                    />
                  );
                })
                .otherwise(() => null)}
            </CarouselItem>
          ))}
        </CarouselContent>
        <div
          className={cn('relative', {
            'md:container': isOverflow,
          })}
        >
          {data.show_nav_arrows ? (
            <div
              className={cn(
                'hidden md:hidden lg:hidden',
                // Even if show_nav_arrows is true, we need to display the arrows just if it's necessary
                {
                  'md:block':
                    maxPerRow > 2 ? itemsCount > 2 : itemsCount > maxPerRow,
                  'lg:block': itemsCount > maxPerRow,
                }
              )}
            >
              <CarouselPrevious
                className={cn(
                  'md:left-7 lg:left-[9.5rem] md:top-1 lg:top-0  text-black CarouselArrows',
                  {
                    'md:left-0 lg:left-0': !isOverflow,
                  }
                )}
              />
              <CarouselNext
                className={cn(
                  'md:right-7 lg:right-[10rem] md:top-1 lg:top-0 text-black CarouselArrows',
                  {
                    'md:right-0 lg:right-2': !isOverflow,
                  }
                )}
              />
            </div>
          ) : null}
          {data.show_nav_dots ? (
            <CarouselIndicators
              className={cn(
                'mt-6 md:mt-8 lg:mt-16 [&_[data-active=true]]:text-black [&_[data-active=false]]:text-light-grey-500 CarouselIndicators',
                {
                  'md:hidden':
                    maxPerRow > 2 ? itemsCount <= 2 : itemsCount <= maxPerRow,
                  'lg:hidden': itemsCount <= maxPerRow,
                }
              )}
            />
          ) : null}
        </div>
      </Carousel>
    </>
  );
};
export { CMSColumnsCarousel };
